import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Map, Zap, Sparkles, Wrench, CheckCircle2, CircleDashed, Hammer, ChevronUp, Lightbulb, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { getVoterId } from "../lib/voter";
import { useAuth } from "../context/AuthContext";
import { usePageMeta } from "../lib/seo";
import { Navbar } from "../components/Navbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

const TAG_META = {
  nieuw: { label: "Nieuw", cls: "bg-emerald-100 text-emerald-700", Icon: Sparkles },
  verbeterd: { label: "Verbeterd", cls: "bg-blue-100 text-blue-700", Icon: Wrench },
  opgelost: { label: "Opgelost", cls: "bg-amber-100 text-amber-700", Icon: CheckCircle2 },
};
const STATUS_META = {
  in_ontwikkeling: { label: "In ontwikkeling", cls: "bg-amber-100 text-amber-700", Icon: Hammer },
  gepland: { label: "Gepland", cls: "bg-gray-200 text-gray-600", Icon: CircleDashed },
  klaar: { label: "Klaar", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
};

const VoteButton = ({ item, endpoint, onVoted, testId }) => {
  const [busy, setBusy] = useState(false);
  const vote = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { voter_id: getVoterId() });
      onVoted(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Stemmen mislukt — probeer opnieuw");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button data-testid={testId} onClick={vote} disabled={busy} title={item.voted ? "Stem intrekken" : "Upvote"}
      className={`flex flex-col items-center justify-center rounded-xl border px-3 py-1.5 min-w-[52px] shrink-0 transition-[background-color,border-color,transform] duration-200 active:scale-95 ${item.voted ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/60 text-foreground"}`}>
      <ChevronUp className="w-4 h-4" strokeWidth={3} />
      <span className="text-sm font-bold font-mono leading-none pb-0.5">{item.votes}</span>
    </button>
  );
};

const IdeaForm = ({ user, onCreated }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 3) return toast.error("Geef je idee een titel (min. 3 tekens)");
    setBusy(true);
    try {
      const { data } = await api.post("/ideas", { title: title.trim(), description: description.trim(), author_name: name.trim(), voter_id: getVoterId() });
      setTitle(""); setDescription(""); setName("");
      onCreated(data);
      toast.success("Bedankt! Je idee staat op het bord — anderen kunnen er nu op stemmen.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="idea-form" className="bg-card rounded-2xl p-5 mb-6 border border-primary/20 space-y-3">
      <p className="font-heading font-bold flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-500" /> Deel jouw idee</p>
      <Input data-testid="idea-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
        placeholder="Wat zou VoltRoute beter maken?" className="rounded-xl h-11" />
      <Textarea data-testid="idea-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={2000}
        placeholder="Licht kort toe (optioneel)" className="rounded-xl" />
      {!user && (
        <Input data-testid="idea-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
          placeholder="Je naam (optioneel)" className="rounded-xl h-10" />
      )}
      <Button data-testid="idea-submit-button" type="submit" disabled={busy} className="rounded-full bg-primary hover:bg-[#1B4332]">
        <Send className="w-4 h-4 mr-1.5" /> {busy ? "Versturen..." : "Plaats idee"}
      </Button>
    </form>
  );
};

export default function UpdatesPage() {
  usePageMeta("Wat is nieuw — VoltRoute updates, roadmap & ideeën", "Alle nieuwe functies en verbeteringen van VoltRoute, stem op de roadmap en deel je eigen ideeën.");
  const { user } = useAuth() || {};
  const [data, setData] = useState(null);
  const [ideas, setIdeas] = useState(null);

  useEffect(() => {
    const voter = getVoterId();
    api.get("/updates", { params: { voter } }).then((r) => {
      setData(r.data);
      if (r.data.latest_date) localStorage.setItem("vr_updates_seen", r.data.latest_date);
    }).catch(() => setData({ changelog: [], roadmap: [] }));
    api.get("/ideas", { params: { voter } }).then((r) => setIdeas(r.data)).catch(() => setIdeas([]));
  }, []);

  const patchRoadmap = useCallback((id, patch) => {
    setData((d) => ({ ...d, roadmap: d.roadmap.map((u) => (u.id === id ? { ...u, ...patch } : u)) }));
  }, []);
  const patchIdea = useCallback((id, patch) => {
    setIdeas((list) => list.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="updates-page">
      {user ? <Navbar /> : (
        <header className="sticky top-0 z-40 glass-panel border-b border-border/60">
          <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-full bg-primary flex items-center justify-center"><Zap className="w-5 h-5 text-primary-foreground" /></span>
              <span className="font-heading font-extrabold text-lg tracking-tight">VoltRoute</span>
            </Link>
            <Link to="/auth" className="text-sm font-semibold text-primary hover:underline">Inloggen</Link>
          </div>
        </header>
      )}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-accent mb-2">VoltRoute evolueert</p>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">Wat is nieuw & roadmap</h1>
        <p className="text-sm text-muted-foreground mb-8">Alles wat we recent lanceerden, wat eraan komt — en jij bepaalt mee wat volgt.</p>

        <Tabs defaultValue="nieuw">
          <TabsList className="rounded-full mb-6">
            <TabsTrigger data-testid="updates-tab-nieuw" value="nieuw" className="rounded-full text-xs"><Megaphone className="w-3.5 h-3.5 mr-1.5" /> Wat is nieuw</TabsTrigger>
            <TabsTrigger data-testid="updates-tab-roadmap" value="roadmap" className="rounded-full text-xs"><Map className="w-3.5 h-3.5 mr-1.5" /> Roadmap</TabsTrigger>
            <TabsTrigger data-testid="updates-tab-ideas" value="ideas" className="rounded-full text-xs"><Lightbulb className="w-3.5 h-3.5 mr-1.5" /> Ideeën</TabsTrigger>
          </TabsList>

          <TabsContent value="nieuw">
            {!data ? <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin" /></div> : (
              <div className="space-y-4" data-testid="changelog-list">
                {data.changelog.length === 0 && <p className="text-sm text-muted-foreground bg-card rounded-2xl p-6 text-center">Nog geen aankondigingen.</p>}
                {data.changelog.map((u) => {
                  const t = TAG_META[u.tag] || TAG_META.nieuw;
                  return (
                    <div key={u.id} data-testid={`changelog-item-${u.id}`} className="bg-card rounded-2xl p-5 relative pl-14">
                      <span className={`absolute left-4 top-5 w-7 h-7 rounded-full flex items-center justify-center ${t.cls}`}><t.Icon className="w-3.5 h-3.5" /></span>
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-0.5 ${t.cls}`}>{t.label}</span>
                        <span className="text-[11px] font-mono text-muted-foreground">{u.date}</span>
                      </div>
                      <p className="font-heading font-bold">{u.title}</p>
                      {u.description && <p className="text-sm text-muted-foreground mt-1 leading-relaxed whitespace-pre-wrap">{u.description}</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="roadmap">
            {!data ? null : (
              <div className="space-y-4" data-testid="roadmap-list">
                <p className="text-xs text-muted-foreground bg-secondary/60 rounded-xl px-4 py-2.5">Stem op wat jij het belangrijkst vindt — de populairste items pakken we eerst op. ▲</p>
                {data.roadmap.length === 0 && <p className="text-sm text-muted-foreground bg-card rounded-2xl p-6 text-center">De roadmap wordt binnenkort gevuld.</p>}
                {data.roadmap.map((u) => {
                  const s = STATUS_META[u.status] || STATUS_META.gepland;
                  return (
                    <div key={u.id} data-testid={`roadmap-item-${u.id}`} className="bg-card rounded-2xl p-5 flex items-start gap-3">
                      <VoteButton item={u} endpoint={`/updates/${u.id}/vote`} testId={`roadmap-vote-${u.id}`}
                        onVoted={(res) => patchRoadmap(u.id, res)} />
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-heading font-bold">{u.title}</p>
                          <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-0.5 ${s.cls}`}>{s.label}</span>
                        </div>
                        {u.description && <p className="text-sm text-muted-foreground mt-1 leading-relaxed whitespace-pre-wrap">{u.description}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="ideas">
            <IdeaForm user={user} onCreated={(idea) => setIdeas((list) => [idea, ...(list || [])])} />
            {!ideas ? <div className="flex justify-center py-10"><div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin" /></div> : (
              <div className="space-y-3" data-testid="ideas-list">
                {ideas.length === 0 && <p className="text-sm text-muted-foreground bg-card rounded-2xl p-6 text-center">Nog geen ideeën — wees de eerste!</p>}
                {ideas.map((i) => (
                  <div key={i.id} data-testid={`idea-item-${i.id}`} className="bg-card rounded-2xl p-4 flex items-start gap-3">
                    <VoteButton item={i} endpoint={`/ideas/${i.id}/vote`} testId={`idea-vote-${i.id}`}
                      onVoted={(res) => patchIdea(i.id, res)} />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold">{i.title}</p>
                        {i.status === "gepland" && (
                          <span data-testid={`idea-promoted-badge-${i.id}`} className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-0.5 bg-emerald-100 text-emerald-700">Op de roadmap 🎉</span>
                        )}
                      </div>
                      {i.description && <p className="text-sm text-muted-foreground mt-1 leading-relaxed whitespace-pre-wrap">{i.description}</p>}
                      <p className="text-[11px] text-muted-foreground mt-1.5 font-semibold">{i.author_name}
                        <span className="font-mono font-normal"> · {i.created_at ? new Date(i.created_at).toLocaleDateString("nl-BE", { day: "numeric", month: "short" }) : ""}</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <p className="text-xs text-muted-foreground mt-10 text-center">
          Bug gevonden? Gebruik de <span className="font-semibold text-[#B5838D]">Feedback</span>-knop links op je scherm (na inloggen).
        </p>
      </main>
    </div>
  );
}
